import type { AxiosError } from 'axios';

import type { UserPhoneNumber } from '@entities/user';
import type { CompanyIntegration } from '@entities/integration';
import type { CompanyIntegrationsClient } from '@usecases/store-integration-settings.usecase';

import type { HttpClientOptions } from '@main/http/http-client';
import { HttpClient } from '@main/http/http-client';
import { HttpRequestInterceptors } from '@main/http/http-request-interceptors';
import { idHttpClient } from '@main/http/id-http-client';

export type CompanyIntegrationHttpResponse = {
  id: number;
  application_id: number;
  status: string;
  number_ids: number[];
};

export class PhoneHttpClient
  extends HttpClient
  implements CompanyIntegrationsClient
{
  private static host = process.env.HOST;
  private salesforceScvAppIds: number[] =
    process.env.SALESFORCE_SCV_APP_ID.split(',').map((id) =>
      parseInt(id.trim()),
    );
  private static baseURl = `https://phone.${this.host}`;

  private static options: HttpClientOptions = {
    baseUrl: this.baseURl,
    retryConfig: {
      preRetryOp: this.preRetryOp,
      retryCondition: this.retryCondition,
    },
  };

  constructor() {
    super(PhoneHttpClient.options);
    this.axiosInstance.interceptors.request.use(
      HttpRequestInterceptors.addBearerTokenInterceptor,
    );
  }

  private static async preRetryOp(error: AxiosError) {
    const { response } = error;
    if (response && (response.status === 403 || response.status == 401)) {
      return idHttpClient.updateToken();
    }
  }

  private static retryCondition(error: AxiosError, retryCount: number) {
    const { message } = error;
    const { response } = error;

    const isNetworkError =
      message?.toLowerCase().includes('timeout') ||
      message?.toLowerCase().includes('network error');

    const isUnAuthenticated =
      (response?.status === 403 || response?.status == 401) && retryCount < 1;

    return isUnAuthenticated || isNetworkError;
  }

  async fetchCompanyIntegrations(
    companyId: number,
    phoneNumbers: UserPhoneNumber[],
  ): Promise<CompanyIntegration[]> {
    const resData = await this.get<CompanyIntegrationHttpResponse[]>(
      `/v3/hub/companies/${companyId}/integrations`,
    );

    return this.filterIntegrations(resData, phoneNumbers);
  }

  /**
   * this method returns only salesforce scv active integrations
   * those belongs to current logged-in user.
   */
  private filterIntegrations(
    integrations: CompanyIntegrationHttpResponse[],
    phoneNumbers: UserPhoneNumber[],
  ): CompanyIntegration[] {
    const phoneNumberIds = phoneNumbers.map((num) => num.id);

    return integrations
      .map(
        ({
          status,
          application_id: applicationId,
          id,
          number_ids: numberIds,
        }: CompanyIntegrationHttpResponse) => ({
          id,
          status,
          applicationId,
          numberIds,
        }),
      )
      .filter((integration) => {
        return (
          this.salesforceScvAppIds.includes(integration.applicationId) &&
          integration.status !== 'inactive' &&
          integration.numberIds.reduce((prev, curr) => {
            return prev || phoneNumberIds.includes(curr);
          }, false)
        );
      });
  }
}

export const phoneHttpClient = new PhoneHttpClient();
